// PayoutsSchedulePage — Withdrawal schedule + history
// Layout:
//   - Top nav: < Back | Payouts
//   - Hero: next payout date + amount
//   - Schedule (Beginner/Mid/Pro)
//   - Payout history
//   - FAQ

import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';
import { DEMO_CHALLENGE } from '../../data/platforms';

interface Payout {
  id: string;
  date: string;
  amount: number;
  method: string;
  status: 'paid' | 'pending' | 'scheduled';
  reference: string;
}

const PAYOUTS: Payout[] = [
  { id: 'p1', date: '2026-07-30', amount: 1280.00, method: 'Bank transfer',     status: 'scheduled', reference: 'PO-2026-07-30' },
  { id: 'p2', date: '2026-07-15', amount:  920.00, method: 'Bank transfer',     status: 'paid',     reference: 'PO-2026-07-15' },
  { id: 'p3', date: '2026-07-01', amount:  425.00, method: 'Bank transfer',     status: 'paid',     reference: 'PO-2026-07-01' },
  { id: 'p4', date: '2026-06-15', amount:  680.00, method: 'USDT (TRC-20)',     status: 'paid',     reference: 'PO-2026-06-15' },
  { id: 'p5', date: '2026-06-01', amount:  340.00, method: 'Bank transfer',     status: 'paid',     reference: 'PO-2026-06-01' },
];

export function PayoutsSchedulePage() {
  const nav = useNavigate();
  const userTier = DEMO_CHALLENGE.tier;
  const tier = userTier;
  // Cadence depends on tier
  const cadence = tier === 'Professional' ? 'Weekly' : tier === 'Mid' ? 'Bi-weekly' : 'Monthly';
  const minDays = tier === 'Professional' ? 5 : tier === 'Mid' ? 5 : 5;
  const split = tier === 'Professional' ? 90 : 80;

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Payouts</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4 overflow-hidden rounded-[20px] bg-gradient-to-br from-[#34C759] to-[#248A3D] p-5 text-white">
        <div className="text-[12px] uppercase tracking-wide text-white/80">Next payout</div>
        <div className="mt-1 text-[28px] font-bold tabular-nums tracking-tight">${PAYOUTS[0].amount.toLocaleString()}</div>
        <div className="mt-1 text-[13px] text-white/90">Scheduled for {new Date(PAYOUTS[0].date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</div>
        <div className="mt-3 grid grid-cols-3 gap-2 border-t border-white/15 pt-3">
          <div>
            <div className="text-[11px] text-white/80">Tier</div>
            <div className="text-[14px] font-semibold"><TierPill tier={userTier} /></div>
          </div>
          <div>
            <div className="text-[11px] text-white/80">Cadence</div>
            <div className="text-[14px] font-semibold">{cadence}</div>
          </div>
          <div>
            <div className="text-[11px] text-white/80">Split</div>
            <div className="text-[14px] font-semibold">{split}%</div>
          </div>
        </div>
      </div>

      {/* Payout rules */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Payout rules</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Rule label="Min funded days" value={`${minDays} profitable days`} />
        <Rule label="Min payout amount" value="$100" />
        <Rule label="Payout method" value="Bank · Wire · USDT · BTC" />
        <Rule label="Processing time" value="1-3 business days" />
        <Rule label="Profit split" value={`${split}% to you / ${100 - split}% to Funder Pro`} last />
      </div>

      {/* History */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">History</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {PAYOUTS.map((p, i) => (
          <div key={p.id} className={['flex items-center gap-3 px-4 py-3', i < PAYOUTS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <div className={['flex h-9 w-9 items-center justify-center rounded-full text-[16px]',
              p.status === 'paid' ? 'bg-[#34C759]/15 text-tf-profit' :
              p.status === 'pending' ? 'bg-[#FF9500]/15 text-tf-warning' :
              'bg-tf-blue/15 text-tf-blue'].join(' ')}>
              {p.status === 'paid' ? '✓' : p.status === 'pending' ? '⏱' : '📅'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1">${p.amount.toFixed(2)}</div>
              <div className="text-[11px] text-tf-text-3">{p.method} · {new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            </div>
            <span className={['rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
              p.status === 'paid' ? 'bg-[#34C759]/15 text-tf-profit' :
              p.status === 'pending' ? 'bg-[#FF9500]/15 text-tf-warning' :
              'bg-tf-blue/15 text-tf-blue'].join(' ')}>
              {p.status}
            </span>
          </div>
        ))}
      </div>

      {/* Upgrade hint for better cadence */}
      {tier === 'Beginner' && (
        <div className="mx-4 mt-4 rounded-[16px] border border-[#007AFF]/30 bg-tf-blue/[0.05] p-4">
          <div className="text-[14px] font-semibold text-tf-blue">💡 Upgrade to Mid for bi-weekly payouts</div>
          <div className="mt-1 text-[12px] text-tf-text-1">Get your profits faster and unlock 3 active challenges.</div>
          <button onClick={() => nav('/portal/tiers')} className="mt-2 text-[13px] font-semibold text-tf-blue">Compare tiers →</button>
        </div>
      )}
    </div>
  );
}

function Rule({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[14px] text-tf-text-3">{label}</span>
      <span className="text-[14px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}
